<script>
export default {
  name: "Housings",
};
</script>

<script setup>
import {
  defineEmits,
  defineProps,
  reactive,
  unref,
  watch,
  computed,
} from "vue";
import { useVModel } from "@vueuse/core";
import { useVuelidate } from "@vuelidate/core";
import { cloneDeep, differenceBy, sortBy } from "lodash";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import BasicSwitch from "@/components/shared/input/BasicSwitch.vue";
import RadioYesNo from "@/components/shared/input/RadioYesNo.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import HousingSelectionModal from "./HousingSelectionModal";
import FromLibrary from "./FromLibrary";
import { filterNonActiveRoomsAndUnits } from "@/components/program-manager/sessions/utils";
import { getLowestProductPrice } from "@/components/program-manager/sessions/utils";

const props = defineProps({
  modelValue: {
    type: Object,
  },
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const rules = {
  programSession: {
    designated_insurance: {},
    group_flight: {},
    public_transportation_card: {},
    extend_hotel_max: {},
    need_hotel: {},
    needs_hotel: {},
  },
};

const v$ = useVuelidate(rules, programSession, {
  $registerAs: "Housings",
  $lazy: true,
});

const state = reactive({
  modalIsOpen: false,
  noDesignatedHousing: false,
  selectedHousing: cloneDeep(props.modelValue.unit_housings),
  canExtendStay: !!props.modelValue.extend_hotel_max,
});

const onNoDesignatedHousing = () => {
  state.noDesignatedHousing = !state.noDesignatedHousing;
};

const addHotel = () => {};
const updateSelectedHousing = (preSelectedHousing) => {
  state.modalIsOpen = false;

  const selectedHousing = filterNonActiveRoomsAndUnits(preSelectedHousing);
  const addedHousing = differenceBy(
    selectedHousing,
    state.selectedHousing,
    "id"
  );
  if (addedHousing?.length > 0) {
    addedHousing.forEach((housing) => {
      if (housing?.units?.length > 0) {
        housing.units.forEach((unit) => {
          if (!unitIsSelected(unit?.id)) {
            unref(programSession)?.session_units.push(unit);
          }
        });
      }
    });
  }
  const removedHousing = differenceBy(
    state.selectedHousing,
    selectedHousing,
    "id"
  );
  removedHousing.forEach((val) => removeHousing(val));
  state.selectedHousing = sortBy(selectedHousing, [
    (o) => {
      return o?.name?.toLowerCase();
    },
  ]);

  unref(programSession).default_room_id = getLowestPriceRoomId();
};

const getLowestPriceRoomId = () => {
  let lowestPriceRoom;
  const roomStartDate = unref(programSession)?.session_travel_details?.[0]
    ?.arrival_date;
  if (
    Array.isArray(state?.selectedHousing) &&
    state?.selectedHousing?.length > 0
  ) {
    for (const housing of state.selectedHousing) {
      if (Array.isArray(housing.units) && housing.units?.length > 0) {
        for (const unit of housing.units) {
          if (Array.isArray(unit.rooms) && unit.rooms?.length > 0) {
            for (const room of unit.rooms) {
              const lowestRoomProductPrice = getLowestProductPrice(
                room,
                roomStartDate
              );
              if (
                !lowestPriceRoom ||
                (typeof lowestRoomProductPrice?.price_in_cents === "number" &&
                  lowestRoomProductPrice.price_in_cents <
                    lowestPriceRoom?.price_in_cents)
              ) {
                lowestPriceRoom = {
                  ...room,
                  price_in_cents: lowestRoomProductPrice.price_in_cents,
                };
              }
            }
          }
        }
      }
    }
  }
  return lowestPriceRoom?.id;
};

const unitIndex = (unitId) => {
  return unref(programSession)?.session_units?.findIndex((val) => {
    return val.id === unitId;
  });
};

const unitIsSelected = (id) => {
  return unitIndex(id) >= 0;
};

const checkUnit = (unit) => {
  if (!unitIsSelected(unit.id)) {
    unref(programSession)?.session_units?.push(unit);
    if (!unref(programSession)?.default_room_id) {
      unref(programSession).default_room_id = getLowestPriceRoomId();
    }
  } else {
    const index = unitIndex(unit.id);
    if (index >= 0) {
      unref(programSession)?.session_units?.splice(index, 1);
    }

    if (unref(programSession).default_room_id && unit?.rooms?.length > 0) {
      for (const room of unit.rooms) {
        if (room.id === unref(programSession).default_room_id) {
          unref(programSession).default_room_id = getLowestPriceRoomId();
          break;
        }
      }
    }
  }
};

const removeHousing = (housing) => {
  if (housing?.units && housing?.units?.length > 0) {
    housing?.units.forEach((unit) => {
      const index = unitIndex(unit.id);
      if (index >= 0) {
        unref(programSession)?.session_units?.splice(index, 1);
      }
      if (unit?.rooms?.length > 0 && unref(programSession).default_room_id) {
        for (const room of unit.rooms) {
          if (room.id === unref(programSession).default_room_id) {
            unref(programSession).default_room_id = null;
            break;
          }
        }
      }
    });
  }
  const housingIndex = state.selectedHousing.findIndex(
    ({ id }) => id === housing.id
  );

  if (housingIndex >= 0) {
    state.selectedHousing.splice(housingIndex, 1); // out of state
  }

  const housingRequestIndex = unref(programSession)?.session_units?.findIndex(
    ({housing_id}) => housing_id === housing.id
  )

  if (housingRequestIndex >= 0) {
    // Fallback removal for inactive housings
    unref(programSession)?.session_units?.splice(housingRequestIndex, 1);
  }

  if (!unref(programSession)?.default_room_id) {
    unref(programSession).default_room_id = getLowestPriceRoomId();
  }
};

const daysOptions = [...Array(10)].map((val, index) => ({
  id: index + 1,
  name: `${index + 1} Day${index > 1 ? "s" : ""}`,
}));

const updateDefaultRoomId = (id) => {
  if (unref(programSession).default_room_id === id) {
    unref(programSession).default_room_id = null;
  } else {
    unref(programSession).default_room_id = id;
  }
};

const sessionHostInstitutions = computed(() => [
  ...(unref(programSession)?.session_hosting_institutions ?? []),
  ...[unref(programSession)?.sponsoring_institutions],
]);

watch(
  () => props.modelValue.unit_housings,
  (newHousing) => {
    state.selectedHousing = newHousing;
  },
  { deep: true }
);

watch(
  () => props.modelValue.extend_hotel_max,
  (extend_hotel_max) => {
    state.canExtendStay = !!extend_hotel_max;
  },
  {}
);
</script>

<template>
  <ExpandingSection id="housing" field-label="Housings">
    <template #content>
      <div class="grid grid-cols-2 gap-x-24 gap-y-4">
        <BasicSwitch
          class="col-span-2"
          field-label="No designated excursions and events"
          @update:modelValue="onNoDesignatedHousing"
        />
        <HousingSelectionModal
          :housingOwnerId="props.modelValue.owner_entity_id"
          :terms="props.modelValue.terms"
          :session-travel-details="props.modelValue.session_travel_details"
          :session-types="props.modelValue.session_types"
          :session-host-institutions="sessionHostInstitutions"
          :modal-is-open="state.modalIsOpen"
          :selected-housing="state.selectedHousing"
          @close-modal="() => (state.modalIsOpen = false)"
          @update-selected-housing="updateSelectedHousing"
        />
        <div class="flex justify-between items-center col-span-2">
          <div class="common-label-text">Housing</div>
          <div
            v-if="!state.noDesignatedHousing"
            class="cursor-pointer"
            @click="state.modalIsOpen = true"
          >
            <PlusIcon
              data-testid="add-housings-modal-open-button"
              class="mr-2 text-teal-900 w-8 h-8"
            />
          </div>
        </div>
        <FromLibrary
          v-if="state?.selectedHousing?.length > 0"
          :selected-housing="state?.selectedHousing"
          :selected-units="programSession?.session_units"
          :default-room="programSession?.default_room_id"
          :session_travel_details="props.modelValue.session_travel_details"
          @remove-housing="removeHousing"
          @check-unit="checkUnit"
          @update-default-room-id="updateDefaultRoomId"
        />
        <RadioYesNo
          v-model="state.canExtendStay"
          class="col-span-1"
          field-label="Can student extend stay after the Program?"
        />
        <div class="col-span-1">
          <VSelectCaret
            v-show="state.canExtendStay"
            v-model="programSession.extend_hotel_max"
            :options="daysOptions"
            :reduce="(val) => val.id"
            label="name"
            :vuelidate-instance="v$.programSession.status_id"
            field-label="For how many days?"
          />
        </div>
        <RadioYesNo
          v-model="programSession.need_hotel"
          class="col-span-1"
          field-label="Need hotel room for orientation?"
        />
        <div class="col-span-2">
          <div
            v-show="programSession.need_hotel"
            class="flex justify-between items-center"
          >
            <div class="common-label-text">Housing</div>
            <div class="cursor-pointer" @click="addHotel">
              <PlusIcon class="mr-2 text-teal-900 w-8 h-8" />
            </div>
          </div>
        </div>
        <RadioYesNo
          v-model="programSession.needs_hotel"
          field-label="Can student opt to book hotel extra days before orientation"
        />
      </div>
    </template>
  </ExpandingSection>
</template>
